/**
 * Portal document-type model for the v2 wire (DEV-6164 / DEV-6863).
 *
 * The wire form is a bare {@link DocumentSubtype} api-value string
 * (e.g. {@code "MedicalBill"}). The BFF derives the parent category from the
 * catalog server-side; clients never send the category. Responses always
 * carry the bare string. The FE catalog mirrors the BFF
 * {@code DocumentSubtype} enum + {@code src/test/resources/document-subtype-spec.csv}.
 */
import type { DocumentCategory } from '../types';

/**
 * The PascalCase api-values that travel on the wire. These match NOVA's
 * {@code Submitted_File.Category} field one-to-one after DEV-6863.
 */
export type DocumentSubtype =
  // INVOICE
  | 'MedicalBill'
  | 'HCFA-1500'
  | 'UB-04'
  | 'PrescriptionBill'
  | 'TransportationBill'
  | 'OtherTypeOfBill'
  | 'NonMedicalBill'
  // SUPPORTING_DOCUMENT
  | 'TravelProof'
  | 'PaymentProof'
  | 'Prescription'
  | 'MedicalReport'
  | 'OtherTypeOfProof'
  | 'TripCancellationProof'
  | 'BaggageProof'
  // VOID_CHEQUE
  | 'CheckSample';
// FORM subtypes (FormMedical, CancellationClaimForm, BaggageClaimForm) are
// intentionally absent — the FE never renders claim forms; the BFF filters them
// out server-side before the wire.

/** Parent category for every subtype. Single source of truth for the FE. */
export const SUBTYPE_CATEGORY_MAP: Record<DocumentSubtype, DocumentCategory> = {
  MedicalBill: 'INVOICE',
  'HCFA-1500': 'INVOICE',
  'UB-04': 'INVOICE',
  PrescriptionBill: 'INVOICE',
  TransportationBill: 'INVOICE',
  OtherTypeOfBill: 'INVOICE',
  NonMedicalBill: 'INVOICE',

  TravelProof: 'SUPPORTING_DOCUMENT',
  PaymentProof: 'SUPPORTING_DOCUMENT',
  Prescription: 'SUPPORTING_DOCUMENT',
  MedicalReport: 'SUPPORTING_DOCUMENT',
  OtherTypeOfProof: 'SUPPORTING_DOCUMENT',
  TripCancellationProof: 'SUPPORTING_DOCUMENT',
  BaggageProof: 'SUPPORTING_DOCUMENT',

  CheckSample: 'VOID_CHEQUE',
};

/** Type guard for the {@link DocumentSubtype} string-literal union. */
export function isDocumentSubtype(value: unknown): value is DocumentSubtype {
  return typeof value === 'string' && value in SUBTYPE_CATEGORY_MAP;
}

/**
 * Wire reference to a document type — the bare {@link DocumentSubtype}
 * api-value as a string. Category is derivable via {@link categoryOf}.
 */
export type DocumentTypeRef = DocumentSubtype;

/**
 * Return the {@link DocumentCategory} for a given {@link DocumentTypeRef}.
 * Replaces the now-removed {@code ref.category} field accessor.
 */
export function categoryOf(ref: DocumentTypeRef): DocumentCategory {
  return SUBTYPE_CATEGORY_MAP[ref];
}

/**
 * Identity factory kept for callsite stability — building a ref from a
 * subtype is just the subtype itself in the string wire form.
 */
export function makeDocumentTypeRef(subtype: DocumentSubtype): DocumentTypeRef {
  return subtype;
}

/** Defensive type guard for wire payloads. */
export function isValidDocumentTypeRef(ref: unknown): ref is DocumentTypeRef {
  return isDocumentSubtype(ref);
}
